import { useState, useEffect } from 'react';
import { InputField, SelectField } from '@jobtarget/ui-library';
import Button from '../Button';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch, faFilter } from '@fortawesome/free-solid-svg-icons';
import JobsService from '../../api/JobsService';
import { DivisionData } from '../../api/JobsService';
import { UserData } from '../../api/JobsService';
import { useApiCall } from '../../utils/useApiCall';

interface SearchSectionProps {
  onSearch: (keyword: string, divisionId: string, userId: string) => void;
}

const SearchSection = ({ onSearch }: SearchSectionProps) => {
  // Track window width for responsive behavior
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    keyword: '',
    division: 'all',
    user: 'all'
  });
  const isMobile = windowWidth < 768; // Standard breakpoint for mobile

  // Use API hook for fetching divisions
  const { 
    data: divisions,
    isLoading: isDivisionsLoading,
    error: divisionsError,
    execute: fetchDivisions
  } = useApiCall<DivisionData[]>(JobsService.getDivisions);

  // Use API hook for fetching users
  const { 
    data: users,
    isLoading: isUsersLoading,
    error: usersError,
    execute: fetchUsers
  } = useApiCall<UserData[]>(JobsService.getUsers);

  // Prepare division options for select field
  const divisionOptions = [
    { text: 'All', value: 'all' },
    ...(Array.isArray(divisions) ? divisions : []).map(division => ({
      text: division.name,
      value: division.id.toString()
    }))
  ];

  // Prepare user options for select field
  const userOptions = [
    { text: 'All', value: 'all' },
    ...(Array.isArray(users) ? users : []).filter(user => user.active) // Only show active users
      .map(user => ({
        text: `${user.contactInfo.firstName} ${user.contactInfo.lastName}`,
        value: user.id.toString()
      }))
  ];

  // Fetch divisions and users when component mounts
  useEffect(() => {
    fetchDivisions();
    fetchUsers();
  }, [fetchDivisions, fetchUsers]);

  // Update window width when it changes
  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
      if (window.innerWidth >= 768) {
        setShowFilters(false); // Reset filter toggle on desktop
      }
    };
    
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const handleSearch = () => {
    // Ensure we have valid values, defaulting to 'all' if needed
    const divisionToUse = filters.division || 'all';
    const userToUse = filters.user || 'all';
    
    // Call the parent component's search handler
    onSearch(filters.keyword, divisionToUse, userToUse);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFilters(prev => ({ ...prev, keyword: e.target.value }));
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  // Updated handlers for SelectField components
  const handleDivisionChange = (event: any) => {
    try {
      // Try to extract the value based on the event structure
      let newValue = 'all';
      
      if (event?.target?.value) {
        // Standard HTML select behavior
        newValue = event.target.value;
      } else if (event?.value) {
        // Object with value property
        newValue = event.value;
      } else if (typeof event === 'string') {
        // Direct string value
        newValue = event;
      }
      
      setFilters(prev => ({ ...prev, division: newValue }));
    } catch (error) {
      setFilters(prev => ({ ...prev, division: 'all' }));
    }
  };

  const handleUserChange = (event: any) => {
    try {
      // Try to extract the value based on the event structure
      let newValue = 'all';
      
      if (event?.target?.value) {
        // Standard HTML select behavior
        newValue = event.target.value;
      } else if (event?.value) {
        // Object with value property
        newValue = event.value;
      } else if (typeof event === 'string') {
        // Direct string value
        newValue = event;
      }
      
      setFilters(prev => ({ ...prev, user: newValue }));
    } catch (error) {
      setFilters(prev => ({ ...prev, user: 'all' }));
    }
  };

  return (
    <div style={{ 
      padding: isMobile ? '0.75rem' : '1rem', 
      borderBottom: '1px solid #e9ecef',
      backgroundColor: '#F8F9FA'
    }}>
      {/* Mobile search with filter toggle */}
      {isMobile && (
        <div style={{ 
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem',
          marginBottom: showFilters ? '1rem' : '0'
        }}>
          <div style={{ flex: 1 }}>
            <InputField 
              label="" 
              id="searchByMobile" 
              name="searchByMobile" 
              placeholder="Enter Keywords" 
              value={filters.keyword}
              onChange={handleInputChange}
              onKeyPress={handleKeyPress}
              onPaste={(e: React.ClipboardEvent<HTMLInputElement>) => {
                setTimeout(() => {
                  if (e.target instanceof HTMLInputElement) {
                    const inputValue = e.target.value;
                    setFilters(prev => ({ ...prev, keyword: inputValue }));
                  }
                }, 0);
              }}
            />
          </div>
          <Button
            text={<FontAwesomeIcon icon={faSearch} />}
            variant="primary"
            onClick={handleSearch}
          />
          <Button
            text={<FontAwesomeIcon icon={faFilter} />}
            variant={showFilters ? "primary" : "outline-secondary"}
            onClick={() => setShowFilters(!showFilters)}
          />
        </div>
      )}

      {/* Mobile filters (collapsible) */}
      {isMobile && showFilters && (
        <div style={{ 
          display: 'flex',
          flexDirection: 'column',
          gap: '0.75rem'
        }}>
          <SelectField 
            label="Division"
            id="divisionMobile" 
            name="divisionMobile" 
            options={divisionOptions}
            handleChange={handleDivisionChange}
            keyType='value'
          />
          
          <SelectField 
            label="Posted By"
            id="postedByMobile" 
            name="postedByMobile" 
            options={userOptions}
            handleChange={handleUserChange}
            keyType='value'
          />
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <Button
              text="Search"
              variant="primary"
              onClick={handleSearch}
            />
          </div>
        </div>
      )}

      {/* Desktop search interface - Updated for better responsiveness */}
      {!isMobile && (
        <div style={{ 
          display: 'flex',
          flexDirection: windowWidth < 992 ? 'column' : 'row',
          gap: '1rem'
        }}>
          <div style={{ 
            display: 'flex', 
            flexDirection: windowWidth < 992 ? 'column' : 'row',
            gap: '1rem',
            flex: 1,
            width: '100%'
          }}>
            <div style={{ flex: windowWidth < 992 ? 'auto' : 1, width: '100%' }}>
              <InputField 
                label="Search By" 
                id="searchBy" 
                name="searchBy" 
                placeholder="Enter Keywords" 
                value={filters.keyword}
                onChange={handleInputChange}
                onKeyPress={handleKeyPress}
                onPaste={(e: React.ClipboardEvent<HTMLInputElement>) => {
                  setTimeout(() => {
                    if (e.target instanceof HTMLInputElement) {
                      const inputValue = e.target.value;
                      setFilters(prev => ({ ...prev, keyword: inputValue }));
                    }
                  }, 0);
                }}
              />
            </div>
            
            <div style={{ flex: windowWidth < 992 ? 'auto' : 1, width: '100%' }}>
              <SelectField 
                label="Division"
                id="division" 
                name="division" 
                options={divisionOptions}
                handleChange={handleDivisionChange}
                keyType='value'
              />
            </div>
            
            <div style={{ flex: windowWidth < 992 ? 'auto' : 1, width: '100%' }}>
              <SelectField 
                label="Posted By"
                id="postedBy" 
                name="postedBy" 
                options={userOptions}
                handleChange={handleUserChange}
                keyType='value'
              />
            </div>
          </div>
          
          <div style={{ 
            display: 'flex', 
            alignItems: windowWidth < 992 ? 'flex-start' : 'flex-end',
            marginTop: windowWidth < 992 ? '0.5rem' : '0'
          }}>
            <Button
              text="Search"
              variant="primary"
              id="search-button"
              onClick={handleSearch}
            />
          </div>
        </div>
      )}
      
      {(divisionsError || usersError) && (
        <div style={{
          backgroundColor: '#f8d7da',
          color: '#721c24',
          padding: '0.5rem',
          marginTop: '0.5rem',
          borderRadius: '0.25rem',
          fontSize: '0.875rem'
        }}>
          {divisionsError && <div>Error loading divisions. Using default options.</div>}
          {usersError && <div>Error loading users. Using default options.</div>}
        </div>
      )}
    </div>
  );
};

export default SearchSection; 